import { TIER_COLORS } from "../utils/tiers";
import { FOLLOW_UPS, getFollowUps } from "../utils/followUps";
import { isDeferred, hasNoFit } from "../utils/placement";

// `contextGroupId` is the group this card is being shown inside. It matters
// for anyone in two clinics: player.groupId only names their primary one, so
// dragging their card out of the second clinic would otherwise empty the
// wrong seat. `clinicCount` drives the "2 clinics" chip.
export default function PlayerCard({ player, draggable: isDraggable = false, showGroup = false, onClick, warn = false, partnerFlag = false, contextGroupId = null, clinicCount = 1 }) {
  const colors = TIER_COLORS[player.tier] || TIER_COLORS["No Group"];

  function handleDragStart(e) {
    e.dataTransfer.setData(
      "text/plain",
      JSON.stringify({ playerId: player.id, fromGroupId: contextGroupId ?? player.groupId })
    );
    e.dataTransfer.effectAllowed = "move";
  }

  return (
    <div
      draggable={isDraggable}
      onDragStart={isDraggable ? handleDragStart : undefined}
      onClick={onClick ? (e) => onClick(e) : undefined}
      className={`
        relative ${colors.bg} ${colors.border} border rounded px-2 py-1.5 text-xs
        ${player.gender === "Male" || player.gender === "Female" ? "pr-5" : ""}
        ${isDraggable ? "cursor-grab active:cursor-grabbing hover:brightness-90" : ""}
        ${onClick ? "cursor-pointer hover:brightness-90" : ""}
        select-none transition-[filter]
      `}
    >
      {player.gender === "Male" && (
        <span className="absolute top-1 right-1.5 text-blue-500 text-sm leading-none" title="Male">♂</span>
      )}
      {player.gender === "Female" && (
        <span className="absolute top-1 right-1.5 text-pink-500 text-sm leading-none" title="Female">♀</span>
      )}
      <div className="font-medium text-gray-800 leading-tight flex items-center gap-1">
        {player.name}
        {warn && (
          <span className="text-amber-500 text-[10px]" title="Not available at this time slot">⚠</span>
        )}
        {partnerFlag && (
          <span className="text-purple-500 text-[10px]" title="Preferred partner not in same group">👥</span>
        )}
      </div>
      <div className="flex flex-wrap gap-1 mt-1">
        {player.age != null && (
          <span className="bg-white bg-opacity-70 text-gray-500 rounded px-1">age {player.age}</span>
        )}
        {showGroup && (
          <span className={`${colors.header} ${colors.text} rounded px-1 font-mono`}>
            {player.groupId ?? "Unassigned"}
          </span>
        )}
        {/* The tier lists show every member of a tier, placed or not, so a
            member sitting the term out would otherwise be indistinguishable
            from one still waiting for a group. */}
        {clinicCount > 1 && (
          <span
            className="bg-blue-100 text-blue-700 rounded px-1 font-medium"
            title={`In ${clinicCount} clinics`}
          >
            {clinicCount} clinics
          </span>
        )}
        {isDeferred(player) && (
          <span
            className="bg-slate-200 text-slate-700 rounded px-1 font-medium"
            title="Waiting for next term — doesn't need a group"
          >
            next term
          </span>
        )}
        {hasNoFit(player) && (
          <span
            className="bg-amber-100 text-amber-800 rounded px-1 font-medium"
            title="Can't make any clinic time — wants to play, but nothing on the schedule fits"
          >
            can't make
          </span>
        )}
        {getFollowUps(player).map(key => {
          const tag = FOLLOW_UPS.find(f => f.key === key);
          return (
            <span key={key} className={`${tag.cardChip} rounded px-1 font-medium`} title={tag.label}>
              {tag.chip}
            </span>
          );
        })}
      </div>
    </div>
  );
}
